import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, FACTS_ORIGIN, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme, updateSelectedSymbolInDropdown } from './../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseNewsAverageSentiment {
    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }



    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
      headerPinned: true,
            type: "Cash and Debt",
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            period: 'Yearly',
            interval: 'thisweek'
        } as any;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        this.attachStyles();
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
                    <div class="headTop">
            <h4 class="title news">${this.getTitleIconSvg()} Average Sentiment by Time of Day</h4>
                        <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                  
                            <i class="pi pi-ellipsis-v"></i>
                        </div>
          </div>
          <div class="inBody">
          <div class="optionsRow">
            <div class="item">
                      <select id="intervalSelect" class="interval-select">
                                <option value="today">Today</option>
                                <option value="thisweek">This Week</option>
                                <option value="thismonth">This Month</option>
                            </select>
            </div>

            </div>

            <div class="chartDv" id="averageSentiments">
                <div id="chartContainer" class="coverDv"></div>
            </div>

          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

        // wire up interval select: default to config.interval (today) and refetch on change
        try {
            const select = this.shadowRoot.querySelector('#intervalSelect') as HTMLSelectElement | null;
            if (select) {
                select.value = (this.config as any).interval || 'thisweek';
                select.addEventListener('change', (ev: Event) => {
                    const val = (ev.target as HTMLSelectElement).value;
                    (this.config as any).interval = val;
                    this.setupChart(this.config.symbol!);
                });
            }
        } catch (err) {
            // swallow any errors attaching the listener
            console.warn('Could not wire interval select', err);
        }
    }


    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol: string): Promise<void> {
        try {
            const interval = (this.config as any).interval || 'thisweek';
            const res = await apiGet({
                method: 'GET',
                url: `${FACTS_ORIGIN}/news/sentiment_by_time_of_day?time=${encodeURIComponent(interval)}`,
                body: { time: interval },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            const rows =
                (Array.isArray(json?.data?.output) && json.data.output) ||
                (Array.isArray(json?.result) && json.result) ||
                (Array.isArray(json?.data) && json.data) ||
                (Array.isArray(json) ? json : []);
            this.currentData = rows;

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }

private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#averageSentiments') as HTMLElement;
    if (!container) return;

    if (!this.currentData || !(window as any).echarts) {
        setTimeout(() => this.updateDisplay(), 100);
        return;
    }

    const hasData = Array.isArray(this.currentData) && this.currentData.length > 0;

    if (hasData) {
        container.setAttribute('data-testing-id', 'average-sentiments');
    } else {
        container.removeAttribute('data-testing-id');
    }

    this.renderChartByType(this.config.type!);
}

    private renderChartByType(type: string): void {
        const container = this.shadowRoot.querySelector('#averageSentiments');
        if (!container) return;

        const html = `
  `;
        // container.innerHTML = html;
        this.drawChartByType();
    }

    private drawChartByType(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        if (!container) return;
        const hasData = Array.isArray(this.currentData) && this.currentData.length > 0;

if (hasData) {
    container.setAttribute('data-testing-id', 'average-sentiments');
} else {
    container.removeAttribute('data-testing-id');
}

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const data = this.currentData;
        if (!data || !Array.isArray(data) || data.length === 0) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        const xAxisData = data.map((item: any) => `${item.hour}:00`);
        const sentimentData = data.map((item: any) => item.avg_sentiment ?? 0);

        // const isBlack = this.config.theme === 'black';
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                ...getEChartsTooltipTheme(widgetBox),
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const sentiment = params[0];
                    const row = data[sentiment.dataIndex] as { article_count?: number } | undefined;
                    const countLine =
                        row != null && typeof row.article_count === 'number'
                            ? `<br/>Articles: ${row.article_count}`
                            : '';
                    return `
                    <b>${sentiment.axisValue}</b><br/>
                    Average Sentiment: ${sentiment.value.toFixed(3)}${countLine}
                `;
                }
            },
            grid: { left: 0, right: 0, top: 10, bottom: 0, containLabel: true },
            xAxis: {
                type: 'category',
                data: xAxisData,
                splitLine: { lineStyle: { color: splitLineColor } },
                axisLabel: { color: textColor, fontSize: fontSize },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                splitLine: { lineStyle: { color: splitLineColor } },
                axisLabel: { color: textColor, fontSize: fontSize }
            },
            series: [
                {
                    name: 'Average Sentiment',
                    type: 'bar',
                    data: sentimentData,
                    barWidth: '55%',
                    itemStyle: {
                        color: '#3cad5f' 
                    }
                }
            ]
        };

        const chart = echarts.init(container);
        chart.setOption(option);
    }


    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="27" height="22" viewBox="0 0 27 22" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M22.6125 22H4.3875C3.26681 22.0001 2.18858 21.5634 1.37399 20.7794C0.559395 19.9955 0.0701982 18.9239 0.00675018 17.7843L2.29482e-08 17.5312V3.09375C-9.45295e-05 2.30983 0.291999 1.5551 0.817249 0.982089C1.3425 0.409082 2.06173 0.0605304 2.8296 0.00687518L3.0375 2.33732e-08H19.9125C20.6822 -9.62801e-05 21.4232 0.297406 21.9858 0.832383C22.5484 1.36736 22.8906 2.09991 22.9433 2.882L22.95 3.09375V4.125H23.9625C24.7322 4.1249 25.4732 4.42241 26.0358 4.95738C26.5984 5.49236 26.9406 6.22491 26.9933 7.007L27 7.21875V17.5312C27.0001 18.6727 26.5713 19.7709 25.8016 20.6006C25.032 21.4302 23.9798 21.9285 22.8609 21.9931L22.6125 22ZM4.3875 19.9375H22.6125C23.2056 19.9374 23.777 19.7102 24.2132 19.3008C24.6494 18.8914 24.9184 18.33 24.9669 17.7279L24.975 17.5312V7.21875C24.975 6.96955 24.8864 6.72878 24.7256 6.54097C24.5647 6.35316 24.3426 6.23102 24.1002 6.19712L23.9625 6.1875H22.95V16.8438C22.95 17.093 22.8614 17.3337 22.7006 17.5215C22.5397 17.7093 22.3176 17.8315 22.0752 17.8654L21.9375 17.875C21.6928 17.875 21.4564 17.7847 21.272 17.6209C21.0876 17.4571 20.9677 17.2309 20.9345 16.984L20.925 16.8438V3.09375C20.925 2.84455 20.8364 2.60378 20.6756 2.41597C20.5147 2.22816 20.2926 2.10602 20.0502 2.07212L19.9125 2.0625H3.0375C2.79283 2.06251 2.55644 2.15276 2.37204 2.31656C2.18765 2.48035 2.06773 2.70661 2.03445 2.9535L2.025 3.09375V17.5312C2.02502 18.1351 2.24798 18.7169 2.64962 19.1612C3.05125 19.6054 3.6022 19.8796 4.1931 19.9292L4.3875 19.9375ZM13.8321 14.4375H17.8902C18.1467 14.4376 18.3937 14.5368 18.5811 14.7152C18.7686 14.8936 18.8826 15.1378 18.9 15.3985C18.9175 15.6591 18.8372 15.9169 18.6753 16.1195C18.5134 16.3222 18.282 16.4548 18.0279 16.4904L17.8902 16.5H13.8321C13.5756 16.4999 13.3286 16.4007 13.1412 16.2223C12.9537 16.0439 12.8398 15.7997 12.8223 15.539C12.8048 15.2784 12.8851 15.0206 13.047 14.818C13.2089 14.6153 13.4403 14.4827 13.6944 14.4471L13.8321 14.4375ZM9.77805 9.63188C10.0466 9.63188 10.3041 9.74052 10.494 9.93392C10.6839 10.1273 10.7906 10.3896 10.7906 10.6631V15.4688C10.7906 15.7423 10.6839 16.0046 10.494 16.198C10.3041 16.3914 10.0466 16.5 9.77805 16.5H5.0598C4.79127 16.5 4.53373 16.3914 4.34385 16.198C4.15397 16.0046 4.0473 15.7423 4.0473 15.4688V10.6631C4.0473 10.3896 4.15397 10.1273 4.34385 9.93392C4.53373 9.74052 4.79127 9.63188 5.0598 9.63188H9.77805ZM8.76555 11.6944H6.0723V14.4375H8.76555V11.6944ZM13.8321 9.63188H17.8902C18.1467 9.63196 18.3937 9.73121 18.5811 9.90959C18.7686 10.088 18.8826 10.3322 18.9 10.5928C18.9175 10.8535 18.8372 11.1112 18.6753 11.3139C18.5134 11.5166 18.282 11.6491 18.0279 11.6848L17.8902 11.6944H13.8321C13.5737 11.6971 13.3242 11.599 13.1344 11.4204C12.9447 11.2417 12.8293 10.996 12.8117 10.7334C12.7941 10.4709 12.8757 10.2114 13.0398 10.0082C13.2039 9.8049 13.4381 9.67324 13.6944 9.64012L13.8321 9.63188ZM5.0598 4.81525H17.8902C18.1486 4.81257 18.3981 4.9106 18.5879 5.08925C18.7776 5.2679 18.893 5.51366 18.9106 5.7762C18.9282 6.03875 18.8466 6.29821 18.6825 6.50147C18.5184 6.70472 18.2842 6.83639 18.0279 6.8695L17.8902 6.87775H5.0598C4.80327 6.87767 4.55633 6.77841 4.36888 6.60004C4.18143 6.42166 4.06745 6.17746 4.04997 5.91679C4.03248 5.65612 4.1128 5.3984 4.27468 5.19571C4.43657 4.99303 4.66796 4.86049 4.9221 4.82487L5.0598 4.81525Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseNewsAverageSentiment: typeof TraderverseNewsAverageSentiment; } }
if (typeof window !== 'undefined') { window.TraderverseNewsAverageSentiment = TraderverseNewsAverageSentiment; }